import localStorageJWTManager from './localStorageJwtManager';
import { HttpError } from 'react-admin';
import { getCurrentLanguage, i18nProvider } from '@/i18n';
import { isLanguageSupported } from '@/i18n/languages';

const apiUrl = import.meta.env.VITE_API_URL;

let cachedPermissions = null;
let cachedRolesKey = null;

const buildPermissions = roles => {
    const list = Array.isArray(roles) ? roles : [];
    return {
        isAdmin: list.includes('ROLE_ADMIN'),
        isClientMaster: list.includes('ROLE_CLIENT_MASTER'),
        isClient: ['ROLE_CLIENT', 'ROLE_CLIENT_MASTER', 'ROLE_POS_OWNER'].some(role => list.includes(role)),
        isServiceMaster: list.includes('ROLE_SERVICE_MASTER'),
        isService: ['ROLE_SERVICE', 'ROLE_SERVICE_MASTER'].some(role => list.includes(role)),
        isBok: list.includes('ROLE_BOK'),
        isPosOwner: list.includes('ROLE_POS_OWNER'),
    };
};

const emptyPermissions = buildPermissions([]);

const getCachedPermissions = () => {
    const roles = localStorageJWTManager.getUserRoles();
    const rolesKey = JSON.stringify(roles ?? []);
    if (cachedPermissions && cachedRolesKey === rolesKey) {
        return cachedPermissions;
    }

    cachedPermissions = roles ? buildPermissions(roles) : emptyPermissions;
    cachedRolesKey = rolesKey;
    return cachedPermissions;
};

const eraseData = () => {
    cachedPermissions = null;
    cachedRolesKey = null;
    localStorageJWTManager.eraseToken();
    localStorageJWTManager.eraseRefreshToken();
    localStorageJWTManager.eraseUserEmail();
    localStorageJWTManager.eraseUserRoles();
};

const saveRedirectAfterLogin = () => {
    const redirect = window.location.pathname + window.location.hash + window.location.search;
    if (redirect !== '/login') {
        localStorageJWTManager.setRedirectAfterLogin(redirect);
    }
};

export const authProvider = {
    login: async ({ email, password }) => {
        const request = new Request(`${apiUrl}/login_check`, {
            method: 'POST',
            body: JSON.stringify({ email, password }),
            headers: new Headers({ 'Content-Type': 'application/json' }),
        });

        const response = await fetch(request);
        if (response.status < 200 || response.status >= 300) {
            throw new Error(response.statusText);
        }
        const { token, refresh_token, data } = await response.json();
        localStorageJWTManager.setToken(token);
        localStorageJWTManager.setRefreshToken(refresh_token);
        localStorageJWTManager.setUserEmail(data);
        localStorageJWTManager.setUserRoles(data);

        const language = getCurrentLanguage();

        if (data?.language && language && isLanguageSupported(data.language) && isLanguageSupported(language) && language !== data.language) {
            localStorageJWTManager.setUpdateLanguageAfterLogin(language);
        }
    },

    logout: () => {
        eraseData();
        return Promise.resolve('/login');
    },

    checkAuth: () => {
        if (localStorageJWTManager.getToken()) {
            return Promise.resolve();
        }
        saveRedirectAfterLogin();

        const userEmail = localStorageJWTManager.getUserEmail();
        const userRoles = localStorageJWTManager.getUserRoles();
        const hasUserData = (userEmail != null && userEmail !== '') || (userRoles != null && Array.isArray(userRoles) && userRoles.length > 0);

        if (hasUserData) {
            const translatedMessage = i18nProvider.translate('auth.sessionExpired');
            return Promise.reject(new Error(translatedMessage));
        } else {
            const error = new Error();
            error.message = false;
            return Promise.reject(error);
        }
    },

    checkError: error => {
        const status = (error && error.status) ?? null;
        if (status === 401 || status === 403) {
            eraseData();
            saveRedirectAfterLogin();
            return Promise.reject(new Error('Authentication required'));
        }

        return Promise.resolve();
    },

    getIdentity: () => {
        if (localStorageJWTManager.getUserEmail()) {
            return Promise.resolve({
                id: localStorageJWTManager.getUserEmail(),
                fullName: localStorageJWTManager.getUserEmail(),
            });
        }

        return Promise.reject(new Error('No user identity found'));
    },

    getPermissions: () => Promise.resolve(getCachedPermissions()),

    refreshToken: async refreshToken => {
        const request = new Request(`${apiUrl}/token/refresh`, {
            method: 'POST',
            body: JSON.stringify({ refresh_token: refreshToken }),
            headers: new Headers({ 'Content-Type': 'application/json' }),
        });

        const response = await fetch(request);
        if (response.status < 200 || response.status >= 300) {
            throw new HttpError(response.statusText, response.status);
        }

        return await response.json();
    },
};
